export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number]; // MuJoCo: w, x, y, z

export function clamp(value: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, value));
}

export function wrap(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

export function quatConjugate(q: ArrayLike<number>): Quat {
  return [q[0] ?? 1, -(q[1] ?? 0), -(q[2] ?? 0), -(q[3] ?? 0)];
}

export function quatMul(a: ArrayLike<number>, b: ArrayLike<number>): Quat {
  const aw = a[0] ?? 1;
  const ax = a[1] ?? 0;
  const ay = a[2] ?? 0;
  const az = a[3] ?? 0;
  const bw = b[0] ?? 1;
  const bx = b[1] ?? 0;
  const by = b[2] ?? 0;
  const bz = b[3] ?? 0;
  return [
    aw * bw - ax * bx - ay * by - az * bz,
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
  ];
}

export function quatRotate(q: ArrayLike<number>, v: ArrayLike<number>): Vec3 {
  const p: Quat = [0, v[0] ?? 0, v[1] ?? 0, v[2] ?? 0];
  const out = quatMul(quatMul(q, p), quatConjugate(q));
  return [out[1], out[2], out[3]];
}

export function yawQuat(yaw: number): Quat {
  return [Math.cos(yaw / 2), 0, 0, Math.sin(yaw / 2)];
}

export function quatYaw(q: ArrayLike<number>): number {
  const w = q[0] ?? 1;
  const x = q[1] ?? 0;
  const y = q[2] ?? 0;
  const z = q[3] ?? 0;
  return Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
}

export function quatToSixD(q: ArrayLike<number>): number[] {
  const w = q[0] ?? 1;
  const x = q[1] ?? 0;
  const y = q[2] ?? 0;
  const z = q[3] ?? 0;
  return [
    1 - 2 * (y * y + z * z),
    2 * (x * y - w * z),
    2 * (x * y + w * z),
    1 - 2 * (x * x + z * z),
    2 * (x * z - w * y),
    2 * (y * z + w * x),
  ];
}

export function slerp(a: ArrayLike<number>, b: ArrayLike<number>, t: number): Quat {
  let dot = 0;
  const bb: number[] = [];
  for (let i = 0; i < 4; i += 1) dot += (a[i] ?? 0) * (b[i] ?? 0);
  const sign = dot < 0 ? -1 : 1;
  dot = Math.abs(dot);
  for (let i = 0; i < 4; i += 1) bb.push(sign * (b[i] ?? 0));
  if (dot > 0.9995) {
    const q = Array.from({ length: 4 }, (_, index) => {
      const value = a[index] ?? 0;
      return value + t * ((bb[index] ?? 0) - value);
    });
    const norm = Math.hypot(...q);
    return q.map((value) => value / norm) as Quat;
  }
  const angle = Math.acos(clamp(dot, -1, 1));
  const sa = Math.sin((1 - t) * angle) / Math.sin(angle);
  const sb = Math.sin(t * angle) / Math.sin(angle);
  return Array.from({ length: 4 }, (_, index) =>
    sa * (a[index] ?? 0) + sb * (bb[index] ?? 0),
  ) as Quat;
}

export function smoothstep(t: number): number {
  const u = clamp(t, 0, 1);
  return u * u * (3 - 2 * u);
}
